import * as THREE from "three";

export const BackgroundShader = {
  uniforms: {
    iTime: { value: 0 },
    iResolution: { value: new THREE.Vector2() },
  },
  vertexShader: `
        varying vec2 vUv;
        void main() {
            vUv = uv;
            gl_Position = vec4(position, 1.0);
        }
    `,
  fragmentShader: `
        uniform float iTime;
        uniform vec2 iResolution;
        varying vec2 vUv;

        #define PI 3.141592
        #define TAU (PI * 2.0)
        #define HEX_COS (0.86602540378443 * 0.5)
        #define HEX_TAN (0.57735026918962 * 0.5)
        #define SIN(x) (sin(x)*.5+.5)

        #define LINE_WIDTH 0.0001
        #define MESH_DENSITY 90.
        #define LAYER_DISTANCE 6.5
        #define hue(v) ( .6 + .6 * cos( 6.3*(v) + vec3(0,23,21) ) )

        float tt;

        mat2 rot2(float a) { return mat2(cos(a), sin(a), -sin(a), cos(a)); }

        float dHex(vec2 p) {
            p = abs(p);
            float c = dot(p, normalize(vec2(1, 1.73)));
            return max(c, p.x);
        }

        float dTri(vec2 p) {
            float a = atan(p.x,p.y)+PI;
            float r = TAU/float(3);
            float d = cos(floor(.5+a/r)*r-a)*length(p);
            return d;
        }

        float hexRing(vec2 p, float r, float s, float blur) {
            float d = dHex(p) - r;
            float c = 1.-smoothstep(0., s, abs(d)-blur);
            return c;
        }

        float triRing(vec2 p, float r, float s, float blur) {
            float d = dTri(p) - r;
            float c = 1.-smoothstep(0., s, abs(d)-blur);
            return c;
        }

        float ring(vec2 p, float r, float s, float blur) {
            float d = length(p) - r;
            float c = 1.-smoothstep(0., s, abs(d)-blur);
            return c;
        }

        vec2 pmod(vec2 pos, float num, out float id) {
            float angle = atan(pos.x, pos.y) + PI / num;
            float split = TAU / num;
            id = floor(angle / split);
            angle = id * split;
            return rot2(angle) * pos;
        }

        float curve(float t, float d) {
            t/=d;
            return mix(floor(t), floor(t)+1., pow(smoothstep(0.,1.,fract(t)), 20.));
        }

        void mapStars(vec2 uv, out vec3 near, out vec3 neighbor) {
            vec2 point;
            near = vec3(1e+4);

            for(float y=-1.0; y<=1.0; y+=2.0) {
                point = vec2(0.0, HEX_COS + y * HEX_TAN * 0.25);
                float dist = distance(uv, point);
                near = near.z < dist ? near : vec3(point, dist);
            }
            
            for(float x=-1.0; x<=1.0; x+=2.0) {
                for(float y=-1.0; y<=1.0; y+=2.0) {
                    for(float both=-1.0; both<=1.0; both+=2.0) {
                        point = vec2(x * 0.125, HEX_COS + y * HEX_COS * 0.5);
                        point.x += both * 0.5 * 0.125 * -x;
                        point.y += both * HEX_TAN * 0.125 * -y;
                        float dist = distance(uv, point);
                        near = near.z < dist ? near : vec3(point, dist);
                    }
                }
            }
            
            neighbor = vec3(1e+4);
            
            for(float y=-1.0; y<=1.0; y+=2.0) {
                point = vec2(0.0, HEX_COS + y * HEX_TAN * 0.25);
                if(near.xy != point) {
                    vec2 center = (point + near.xy) * 0.5;
                    float dist = dot(uv - center, normalize(near.xy - point));
                    neighbor = neighbor.z < dist ? neighbor : vec3(point, dist);
                }
            }
            
            for(float x=-1.0; x<=1.0; x+=2.0) {
                for(float y=-1.0; y<=1.0; y+=2.0) {
                    for(float both=-1.0; both<=1.0; both+=2.0) {
                        point = vec2(x * 0.125, HEX_COS + y * HEX_COS * 0.5);
                        point.x += both * 0.5 * 0.125 * -x;
                        point.y += both * HEX_TAN * 0.125 * -y;
                        
                        if(near.xy != point) {
                            vec2 center = (point + near.xy) * 0.5;
                            float dist = dot(uv - center, normalize(near.xy - point));
                            neighbor = neighbor.z < dist ? neighbor : vec3(point, dist);
                        }
                    }
                }
            }
        }

        vec2 toLogPolar(vec2 p) {
            return vec2(log(length(p)), atan(p.y, p.x));
        }

        float starPattern(vec2 uv) {
            vec2 uvb = uv;
            float width = 0.0001 + mix(0.03, 0.001, min(1.0, pow(dot(uv, uv), .3)));
            
            uv = toLogPolar(uv*.01)*2.5;
            uv.x += -.2*tt;
            
            uv = vec2(mod(uv.x, 1.0) - 0.5,
                     mod(uv.y, HEX_COS * 2.0) - HEX_COS);
            
            float id;
            float reps = 5.;
            float t = .07*(tt+6.);
            float modid = (mod(floor(.1*min(20.0, length(uv))-t), reps)+3.)*2.;
            float modt = pow(smoothstep(.0, .3, abs(fract(.1*min(20.0, length(uvb))-t)-.5)), 500.);
            
            float alpha = mix(6., 18., modt);
            uv = pmod(uv, alpha, id);
               
            vec3 near, neighbor;
            mapStars(uv, near, neighbor);
         
            float line = (1.0 - smoothstep(0.0, width, neighbor.z));
            
            return line;
        }

        vec2 moda(vec2 p, float repetitions) {
            float angle = 2.*PI/repetitions;
            float a = atan(p.y, p.x) + angle/2.;
            a = mod(a,angle) - angle/2.;
            return vec2(cos(a), sin(a))*length(p);
        }

        vec3 rings(vec2 uv) {
            float rings = 0.;
            vec2 uvr = uv;
            uvr *= 0.8;

            float triSize = 0.286;
            float hexSize = 0.5;
            float circleSize = .143;
            float lineWidth = 0.0015;
            float blur = 0.001;
            float glow = .3;
            float animr = 0.042;
            vec3 ringc = vec3(0.761,0.851,1.000);

            rings += hexRing(uvr, hexSize, lineWidth, blur); 
            rings += glow*hexRing(uvr, hexSize, lineWidth*5., blur);

            vec2 uv0 = uvr;
            uv0 *= rot2(-PI*animr*curve(tt, 2.));
            rings += hexRing(uv0, hexSize*.5, lineWidth, blur);
            rings += glow*hexRing(uv0, hexSize*.5, lineWidth*5., blur);

            vec2 uv1 = uvr;
            uv1 *= rot2(PI*animr*(curve(tt, 2.)));
            rings += triRing(uv1, .286, lineWidth, blur);
            rings += glow*triRing(uv1, .286, lineWidth*5., blur);

            vec2 uv2 = uvr;
            uv2 *= rot2(PI-animr*PI*((curve(tt, 2.))));
            rings += triRing(uv2, triSize, lineWidth, blur);
            rings += glow*triRing(uv2, triSize, lineWidth*5., blur);

            vec2 uv3 = uvr;
            uv3 *= rot2(PI*.5);
            uv3 *= rot2(PI*animr*curve(tt, 2.));
            uv3 = moda(uv3, 6.);
            uv3.x -= mix(triSize, .576, abs(mod(curve(tt, 2.), 2.)-1.));
            rings += ring(uv3, circleSize, lineWidth, blur);
            rings += glow*ring(uv3, circleSize, lineWidth*5., blur);

            vec3 col = .6*mix(ringc, hue(-tt+min(2.0, length(uv))*2.+.5*PI), .5)*rings*mix(.0, .5, SIN(tt*PI));
            return col;
        }

        float smoothRectVignette(vec2 uv) {
            vec2 d = abs(uv - 0.5) * 2.0;
            
            float dx = smoothstep(0.4, 1.6, d.x);
            float dy = smoothstep(0.4, 1.6, d.y);
            
            float rect = max(dx, dy);
            
            float radial = smoothstep(0.6, 1.5, length(d));
            
            return 1.0 - mix(rect, radial, 0.4);
        }

        void main() {
            vec2 uv = (vUv - 0.5) * 1.8;
            uv.x *= iResolution.x/iResolution.y;
            
            tt = .3*iTime;
            
            vec3 col = vec3(0);
            
            float stars = starPattern(uv);
            col += stars * 0.15 * (.1+.9*hue(-tt+min(2.0, length(uv)))*(mix(.1, .2, SIN(min(2.0, length(uv))*1.5+tt))));
            
            col += .175 * rings(uv);
            
            col = pow(col, vec3(.6));
            
            float vignette = smoothRectVignette(vUv);
            
            col.r *= vignette;
            col.g *= vignette;
            col.b *= vignette;
            
            gl_FragColor = vec4(col, 0.25);
        }
    `,
};
